// The arithmetic behind a per-file placement override, kept pure so it
// can be tested without a viewer.
//
// FRAMES. The owner thinks in Revit's frame: X east, Y north, Z up, in
// the project's export unit. The scene is the exporter's glTF frame:
// Y up, feet, with Revit (x, y, z) -> glTF (x, z, -y). An override is
// built in the Revit frame as T(translation) * R(rotation) * S(scale)
// about a pivot, then conjugated into the glTF frame with the
// change-of-basis matrix C, so the owner never has to know the scene is
// Y-up: "100 feet down in Z" is -100 in the Z box, whatever the frame.
//
// PIVOT. Rotation and scale are about the model's centre, not the world
// origin: this site sits six hundred feet from the origin, and a five
// degree turn about the origin would swing a file across the road.
import { Euler, Matrix4, Vector3 } from 'three';

export const FEET_PER_METRE = 3.280839895;

// Revit -> glTF: x' = x, y' = z, z' = -y. Its inverse is its transpose.
export const REVIT_TO_GLTF = new Matrix4().set(
  1, 0, 0, 0,
  0, 0, 1, 0,
  0, -1, 0, 0,
  0, 0, 0, 1,
);
export const GLTF_TO_REVIT = new Matrix4().copy(REVIT_TO_GLTF).transpose();

export const toFeet = (value, units) => (
  String(units || '').toUpperCase() === 'METRIC' ? Number(value || 0) * FEET_PER_METRE : Number(value || 0)
);

const rad = (deg) => (Number(deg || 0) * Math.PI) / 180;

// Does an entry change anything at all?
export function isIdentityEntry(e) {
  if (!e) return true;
  const t = e.translation || {}; const r = e.rotationDeg || {};
  const s = e.scale === undefined || e.scale === null ? 1 : Number(e.scale);
  return e.visible !== false && !Number(t.x) && !Number(t.y) && !Number(t.z)
    && !Number(r.x) && !Number(r.y) && !Number(r.z) && s === 1;
}

// The override as a matrix in the glTF frame. `pivotGltf` is a Vector3
// in scene coordinates (the model's centre); translation-only entries
// do not depend on it.
export function buildOverrideMatrix(entry, pivotGltf) {
  const t = entry.translation || {};
  const r = entry.rotationDeg || {};
  const s = entry.scale === undefined || entry.scale === null ? 1 : Number(entry.scale) || 1;
  const units = entry.units;

  const core = new Matrix4()
    .makeTranslation(toFeet(t.x, units), toFeet(t.y, units), toFeet(t.z, units))
    .multiply(new Matrix4().makeRotationFromEuler(new Euler(rad(r.x), rad(r.y), rad(r.z), 'XYZ')))
    .multiply(new Matrix4().makeScale(s, s, s));
  // Into the scene's frame.
  const inScene = new Matrix4().copy(REVIT_TO_GLTF).multiply(core).multiply(GLTF_TO_REVIT);
  // About the pivot.
  const p = pivotGltf || new Vector3();
  return new Matrix4().makeTranslation(p.x, p.y, p.z)
    .multiply(inScene)
    .multiply(new Matrix4().makeTranslation(-p.x, -p.y, -p.z));
}

// Which manifest document an entry names. Names are compared without
// case and, failing that, without directories and the extension, so
// "BA_03_SOLAR_MD_B3D.rvt" also matches "ba_03_solar_md_b3d".
const stem = (n) => String(n || '').split(/[\\/]/).pop().replace(/\.rvt$/i, '').trim().toLowerCase();
export function matchDocumentIndex(entry, documents) {
  const docs = Array.isArray(documents) ? documents : [];
  if (entry && entry.docId) {
    const i = docs.findIndex((d) => d && String(d.id ?? d.hash ?? '') === String(entry.docId));
    if (i >= 0) return i;
  }
  const want = String(entry && entry.docName || '').trim().toLowerCase();
  if (!want) return -1;
  let i = docs.findIndex((d) => d && String(d.name || '').trim().toLowerCase() === want);
  if (i >= 0) return i;
  const ws = stem(want);
  i = docs.findIndex((d) => d && stem(d.name) === ws);
  return i;
}
